import { useEffect, useState } from 'react'
import { botApi, type BacktestResult, type BotCode } from '../api'
import BotBacktestMain from './BotBacktestMain'
import BotBacktestPlaceholder from './BotBacktestPlaceholder'
import type { BottomTab } from './BacktestBottomPanel'

interface BotBacktestProps {
  bid: string
  refreshTrigger?: number
  currentCode?: BotCode | null
  hasCode?: boolean
  activeBottomTab?: BottomTab
  onActiveBottomTabChange?: (tab: BottomTab) => void
}

export default function BotBacktest({
  bid,
  refreshTrigger = 0,
  currentCode,
  hasCode,
  activeBottomTab,
  onActiveBottomTabChange,
}: BotBacktestProps) {
  const [backtest, setBacktest] = useState<BacktestResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setBacktest(null)
    setErrorMessage(null)

    botApi.getLatestBacktest(bid)
      .then(data => {
        if (cancelled) return
        setBacktest(data)
      })
      .catch(err => {
        if (cancelled) return
        setErrorMessage(err?.message || 'Failed to load latest backtest.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [bid, refreshTrigger])

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center h-full text-sm" style={{ color: 'var(--color-error)' }}>
        {errorMessage}
      </div>
    )
  }

  if (isLoading || !backtest || backtest.status === 0) {
    return (
      <BotBacktestPlaceholder
        currentCode={currentCode}
        hasCode={hasCode}
        activeBottomTab={activeBottomTab}
        onActiveBottomTabChange={onActiveBottomTabChange}
      />
    )
  }

  if (backtest.status === 200) {
    const errorDetail = backtest.resultSummary?.error
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
        <span className="text-sm font-medium" style={{ color: 'var(--color-error)' }}>
          Backtest failed
        </span>
        <span className="text-sm text-center" style={{ color: 'var(--color-text-grey)' }}>
          {errorDetail || 'An unexpected error occurred during backtest execution. Please try running the backtest again.'}
        </span>
      </div>
    )
  }

  if (backtest.status === 201) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-grey)' }}>
          Backtest canceled
        </span>
        <span className="text-sm text-center" style={{ color: 'var(--color-text-grey)' }}>
          This backtest was canceled before it could complete. You can run a new backtest at any time.
        </span>
      </div>
    )
  }

  return (
    <BotBacktestMain
      btid={backtest.btid}
      backtest={backtest}
      currentCode={currentCode}
      hasCode={hasCode}
      activeBottomTab={activeBottomTab}
      onActiveBottomTabChange={onActiveBottomTabChange}
    />
  )
}
